/**
 * Read-only calendars (internet subscriptions, holidays, birthdays), iCalendar import/export and published links:
 * API surface, real implementation and mock; `withVirtualCalendars` merges the read-only calendars into the normal
 * calendar list/events calls, so every calendar view shows them without knowing where they come from.
 */
import type { CalendarImportResult, CalendarInfo, CalendarPublication, PublishInput, SubscriptionInput, SubscriptionPatch, VirtualCalendars, VirtualOptions, WebmailClient } from '@platform/api-client';
import type { CalendarEvent } from '@platform/api-client';

export type { CalendarPublication, VirtualCalendars };
type Color = CalendarInfo['color'];
type EventsResult = { calendarId: string; events: CalendarEvent[]; error?: { code: string; message: string } | undefined };

export interface CalendarFeedsApi {
  virtualCalendars(): Promise<VirtualCalendars>;
  setVirtualOptions(options: VirtualOptions): Promise<VirtualCalendars>;
  subscribeCalendar(input: SubscriptionInput): Promise<CalendarInfo>;
  patchSubscription(id: string, patch: SubscriptionPatch): Promise<void>;
  refreshSubscription(id: string): Promise<void>;
  unsubscribeCalendar(id: string): Promise<void>;
  setVirtualColor(calendarId: string, color: Color): Promise<void>;
  virtualEvents(calendarIds: readonly string[], start: string, end: string, zone: string): Promise<EventsResult[]>;
  /** Download links (session cookie): one event / a calendar range as .ics. */
  eventIcsUrl(eventId: string): string;
  calendarIcsUrl(calendarId: string, start: string, end: string): string;
  importIcs(calendarId: string, ics: string, timeZone: string): Promise<CalendarImportResult>;
  calendarPublications(): Promise<CalendarPublication[]>;
  publishCalendar(input: PublishInput): Promise<CalendarPublication>;
  refreshPublications(): Promise<void>;
  unpublishCalendar(token: string): Promise<void>;
}

export const isVirtualCalendarId = (id: string): boolean => /^(s|h|b)\./.test(id);

export function createRealCalendarFeeds(client: WebmailClient): CalendarFeedsApi {
  return {
    virtualCalendars: () => client.virtualCalendars(),
    setVirtualOptions: (options) => client.setVirtualOptions(options),
    subscribeCalendar: (input) => client.subscribeCalendar(input),
    patchSubscription: async (id, patch) => {
      await client.patchSubscription(id, patch);
    },
    refreshSubscription: async (id) => {
      await client.refreshSubscription(id);
    },
    unsubscribeCalendar: async (id) => {
      await client.unsubscribeCalendar(id);
    },
    setVirtualColor: async (calendarId, color) => {
      await client.setVirtualColor(calendarId, color);
    },
    virtualEvents: async (ids, start, end, zone) => (await client.virtualEvents(ids.join(','), start, end, zone)).results,
    eventIcsUrl: (eventId) => client.calendarEventIcsUrl(eventId),
    calendarIcsUrl: (calendarId, start, end) => client.calendarExportUrl(calendarId, start, end),
    importIcs: (calendarId, ics, timeZone) => client.importIcs(calendarId, ics, timeZone),
    calendarPublications: async () => (await client.calendarPublications()).publications,
    publishCalendar: (input) => client.publishCalendar(input),
    refreshPublications: async () => {
      await client.refreshPublications();
    },
    unpublishCalendar: async (token) => {
      await client.unpublishCalendar(token);
    },
  };
}

const delay = <T>(value: T, ms = 60): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), ms));

/** Preview mode: subscriptions and options are kept in memory; read-only calendars have no events. */
export function createMockCalendarFeeds(): CalendarFeedsApi {
  let state: VirtualCalendars = { calendars: [], subscriptions: [], holidays: [], countries: ['DE', 'AT', 'CH', 'US', 'GB', 'FR', 'NL', 'IT', 'ES'], birthdays: false };
  let publications: CalendarPublication[] = [];
  const base = { access: 'reviewer' as const, canCreate: false, canShare: false };
  const rebuild = () => {
    state = {
      ...state,
      calendars: [
        ...state.subscriptions.map((s): CalendarInfo => ({ ...base, id: s.calendarId, kind: 'subscription', owner: { name: '', address: '' }, name: new URL(s.url).hostname, color: 'purple' })),
        ...state.holidays.map((cc): CalendarInfo => ({ ...base, id: `h.${cc.toLowerCase()}`, kind: 'holiday', owner: { name: cc, address: '' }, name: cc, color: 'teal' })),
        ...(state.birthdays ? [{ ...base, id: 'b.contacts', kind: 'birthday' as const, owner: { name: '', address: '' }, name: '', color: 'pink' as Color }] : []),
      ],
    };
  };
  return {
    virtualCalendars: () => delay(state),
    setVirtualOptions: (options) => {
      state = { ...state, ...(options.holidays && { holidays: options.holidays }), ...(options.birthdays !== undefined && { birthdays: options.birthdays }) };
      rebuild();
      return delay(state);
    },
    subscribeCalendar: (input) => {
      const id = Math.random().toString(36).slice(2, 12);
      state = { ...state, subscriptions: [...state.subscriptions, { id, calendarId: `s.${id}`, url: input.url.replace(/^webcal/i, 'https'), refreshMinutes: 60 }] };
      rebuild();
      return delay(state.calendars.find((c) => c.id === `s.${id}`) as CalendarInfo);
    },
    patchSubscription: () => delay(undefined),
    refreshSubscription: () => delay(undefined),
    unsubscribeCalendar: (id) => {
      state = { ...state, subscriptions: state.subscriptions.filter((s) => s.id !== id) };
      rebuild();
      return delay(undefined);
    },
    setVirtualColor: () => delay(undefined),
    virtualEvents: (ids) => delay(ids.map((calendarId) => ({ calendarId, events: [] }))),
    eventIcsUrl: () => '#',
    calendarIcsUrl: () => '#',
    importIcs: () => delay({ created: 0, failed: 0, skipped: 0 }),
    calendarPublications: () => delay(publications),
    publishCalendar: (input) => {
      const token = Math.random().toString(36).slice(2).padEnd(32, 'x').slice(0, 32);
      const now = new Date().toISOString();
      const p = { token, calendarId: input.calendar, detail: input.detail ?? 'full', createdAt: now, refreshedAt: now, path: `/api/v1/calendar-feeds/${token}` };
      publications = [...publications, p];
      return delay(p);
    },
    refreshPublications: () => delay(undefined),
    unpublishCalendar: (token) => {
      publications = publications.filter((p) => p.token !== token);
      return delay(undefined);
    },
  };
}

type Core = {
  listCalendars(): Promise<CalendarInfo[]>;
  listEvents(ids: readonly string[], start: string, end: string, zone?: string): Promise<EventsResult[]>;
  updateCalendar(id: string, changes: { name?: string; color?: Color }): Promise<CalendarInfo>;
  removeCalendar(id: string): Promise<void>;
} & CalendarFeedsApi;

/** The read-only calendars join the normal list and event calls; their colour/remove go to their own endpoints. */
export function withVirtualCalendars<T extends Core>(api: T): T {
  const listCalendars = api.listCalendars.bind(api);
  const listEvents = api.listEvents.bind(api);
  const updateCalendar = api.updateCalendar.bind(api);
  const removeCalendar = api.removeCalendar.bind(api);
  return {
    ...api,
    listCalendars: async () => {
      const [own, virtual] = await Promise.all([listCalendars(), api.virtualCalendars().catch(() => undefined)]);
      return [...own, ...(virtual?.calendars ?? [])];
    },
    listEvents: async (ids: readonly string[], start: string, end: string, zone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
      const real = ids.filter((id) => !isVirtualCalendarId(id));
      const virtual = ids.filter(isVirtualCalendarId);
      const [a, b] = await Promise.all([real.length > 0 ? listEvents(real, start, end) : Promise.resolve([]), virtual.length > 0 ? api.virtualEvents(virtual, start, end, zone) : Promise.resolve([])]);
      return [...a, ...b];
    },
    updateCalendar: async (id: string, changes: { name?: string; color?: Color }) => {
      if (!isVirtualCalendarId(id)) return updateCalendar(id, changes);
      if (id.startsWith('s.') && changes.name) await api.patchSubscription(id.slice(2), { name: changes.name, ...(changes.color && { color: changes.color }) });
      else if (changes.color) await api.setVirtualColor(id, changes.color);
      return (await api.virtualCalendars()).calendars.find((c) => c.id === id) as CalendarInfo;
    },
    removeCalendar: async (id: string) => {
      if (!isVirtualCalendarId(id)) return removeCalendar(id);
      if (id.startsWith('s.')) {
        await api.unsubscribeCalendar(id.slice(2));
        return;
      }
      const current = await api.virtualCalendars();
      await api.setVirtualOptions(id.startsWith('h.') ? { holidays: current.holidays.filter((cc) => `h.${cc.toLowerCase()}` !== id) } : { birthdays: false });
    },
  };
}
